// Auto-generated by cli-contracts. Do not edit.
import { Command } from "commander";
import { commandDefinitions, deriveCommandPolicy } from "./policy.js";
import { CONTRACT_YAML, CONTRACT_JSON_STR } from "./contract.js";

export interface CommandHandlers {
  compile: (file: string | undefined, options: { output?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  generate: (file: string | undefined, options: { outputDir?: string; targets?: string; dryRun?: boolean }, parentOpts: Record<string, unknown>) => Promise<void>;
  validate: (file: string | undefined, options: { strict?: boolean }, parentOpts: Record<string, unknown>) => Promise<void>;
  introspect: (file: string | undefined, options: { format?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  init: (projectRoot: string | undefined, options: { projectYaml?: string; binaryPath?: string; bootstrap?: boolean; noLlm?: boolean; artifactsOnly?: boolean }, parentOpts: Record<string, unknown>) => Promise<void>;
  update: (projectRoot: string | undefined, options: { projectYaml?: string }, parentOpts: Record<string, unknown>) => Promise<void>;
  uninstall: (projectRoot: string | undefined, options: Record<string, never>, parentOpts: Record<string, unknown>) => Promise<void>;
  install: (options: { global?: boolean; dryRun?: boolean; skipCursor?: boolean; skipClaude?: boolean; skipGit?: boolean }, parentOpts: Record<string, unknown>) => Promise<void>;
}

export function createProgram(
  handlers: CommandHandlers,
  version: string,
): Command {
  const program = new Command();
  program
    .name("aaac")
    .version(version, "-V, --version")
    .description("AaaC CLI — compile Component Contracts and generate adapters");

  program.option("-v, --verbose", "Enable verbose output");
  program.option("-q, --quiet", "Suppress non-essential output");
  program.option("--resume <value>", "Resume from a previous memory_ref ID");
  program.option("--introspect", "Output derived policy as JSON without executing the command");

  program
    .command("compile")
    .description("Compile a Component Contract YAML to Component IR")
    .argument("<file>", "Path to the Component Contract YAML file")
    .option("-o, --output <value>", "Output path for the compiled IR (JSON)")
    .action(async (file, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("compile", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.compile(file, opts, globalOpts);
    });

  program
    .command("generate")
    .description("Generate adapters from a Component Contract")
    .argument("<file>", "Path to the Component Contract YAML file")
    .option("-o, --output-dir <value>", "Output directory for generated files")
    .option("-t, --targets <value>", "Generation targets (defaults to projections in contract)")
    .option("--dry-run", "Show what would be generated without writing files")
    .action(async (file, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("generate", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.generate(file, opts, globalOpts);
    });

  program
    .command("validate")
    .description("Validate a Component Contract YAML")
    .argument("<file>", "Path to the Component Contract YAML file")
    .option("--strict", "Enable strict validation (check implementation refs)")
    .action(async (file, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("validate", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.validate(file, opts, globalOpts);
    });

  program
    .command("introspect")
    .description("Dump Component IR information")
    .argument("<file>", "Path to the Component Contract YAML file")
    .option("-f, --format <value>", "Output format (default yaml)")
    .action(async (file, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("introspect", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.introspect(file, opts, globalOpts);
    });

  program
    .command("init")
    .description("Initialize a project — copy CLI binary, scaffold project.yaml, materialize git hooks")
    .argument("[projectRoot]", "Project root directory (default: current directory)")
    .option("--project-yaml <value>", "Path to project.yaml relative to projectRoot (default project.yaml)")
    .option("--binary-path <value>", "Path to the CLI binary to copy (default: process.argv[1]); for programmatic/test use")
    .option("--bootstrap", "Run governance bootstrap after init (probe, artifact-contracts, manifest)")
    .option("--no-llm", "Bootstrap with deterministic probe + templates only (no LLM discover)")
    .option("--artifacts-only", "Bootstrap artifact-contracts.yaml only; preserve existing project.yaml")
    .action(async (projectRoot, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("init", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.init(projectRoot, opts, globalOpts);
    });

  program
    .command("update")
    .description("Re-read project.yaml and re-materialize git hooks")
    .argument("[projectRoot]", "Project root directory (default: current directory)")
    .option("--project-yaml <value>", "Path to project.yaml relative to projectRoot (default project.yaml)")
    .action(async (projectRoot, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("update", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.update(projectRoot, opts, globalOpts);
    });

  program
    .command("uninstall")
    .description("Remove all managed hook blocks recorded in governance-manifest.lock")
    .argument("[projectRoot]", "Project root directory (default: current directory)")
    .action(async (projectRoot, opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("uninstall", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.uninstall(projectRoot, {}, globalOpts);
    });

  program
    .command("install")
    .description("Install user-global observability hooks (Cursor + Claude + Git) in one command — idempotent, non-destructive")
    .option("--global", "Install at user scope (~/.cursor, ~/.claude, git --global). Currently the only supported scope.")
    .option("--dry-run", "Preview the changes without writing any files or git config")
    .option("--skip-cursor", "Do not configure the Cursor user hooks")
    .option("--skip-claude", "Do not configure the Claude Code user hooks")
    .option("--skip-git", "Do not configure the global git hooks / core.hooksPath")
    .action(async (opts, cmd) => {
      const globalOpts = cmd.optsWithGlobals();
      if (globalOpts.introspect) {
        const policy = deriveCommandPolicy("install", opts);
        console.log(JSON.stringify(policy, null, 2));
        return;
      }
      await handlers.install(opts, globalOpts);
    });


  // Built-in extract command (auto-injected by cli-contracts)
  program
    .command("extract")
    .description("Extract contract specification for this CLI tool.")
    .argument("[commands...]", "Command IDs to extract. Use dot notation.")
    .option("-a, --all", "Extract all commands.", false)
    .option("--include-meta", "Include extraction metadata.", true)
    .option("-F, --format <format>", "Output format (yaml or json).", "yaml")
    .action(async (commands: string[], opts: { all?: boolean; includeMeta?: boolean; format?: string }, cmd: Command) => {
      if (commands.length === 0 && !opts.all) {
        process.stderr.write(JSON.stringify({ code: "INVALID_ARGS", message: "Specify command IDs or use --all" }) + "\n");
        process.exit(2);
      }

      const format = opts.format || "yaml";
      const doc = JSON.parse(CONTRACT_JSON_STR);
      const cmdIds = opts.all ? [] : commands;

      if (cmdIds.length === 0) {
        // --all: output full contract
        if (format === "json") {
          const out: Record<string, unknown> = {};
          if (opts.includeMeta) {
            out._meta = {
              source: "embedded",
              type: "cli-contracts/extract",
              extractedAt: new Date().toISOString(),
              specVersion: doc.cli_contracts ?? "0.1.0",
              commands: ["aaac.compile","aaac.generate","aaac.validate","aaac.introspect","aaac.init","aaac.update","aaac.uninstall","aaac.install"],
            };
          }
          Object.assign(out, doc);
          process.stdout.write(JSON.stringify(out, null, 2) + "\n");
        } else {
          // YAML output
          const yamlLines: string[] = [];
          yamlLines.push("# aaac extract");
          yamlLines.push("# source: embedded");
          yamlLines.push("# type: cli-contracts/command-extract");
          if (opts.includeMeta) {
            yamlLines.push("---");
            yamlLines.push("source: embedded");
            yamlLines.push("type: cli-contracts/command-extract");
            yamlLines.push("extractedAt: " + new Date().toISOString());
            yamlLines.push("spec_version: " + (doc.cli_contracts ?? "0.1.0"));
            yamlLines.push("commands:");
            for (const id of ["aaac.compile","aaac.generate","aaac.validate","aaac.introspect","aaac.init","aaac.update","aaac.uninstall","aaac.install"]) {
              yamlLines.push("  - " + id);
            }
          }
          yamlLines.push("---");
          yamlLines.push(CONTRACT_YAML);
          process.stdout.write(yamlLines.join("\n") + "\n");
        }
      } else {
        // Filter specific commands
        const filtered: Record<string, unknown> = {
          cli_contracts: doc.cli_contracts,
          info: doc.info,
          command_sets: {},
        };
        const fcs = filtered.command_sets as Record<string, Record<string, unknown>>;
        for (const [setId, cs] of Object.entries(doc.command_sets ?? {})) {
          const cmds = (cs as Record<string, unknown>).commands as Record<string, unknown> | undefined;
          if (!cmds) continue;
          const matched: Record<string, unknown> = {};
          for (const [cmdId, cmdDef] of Object.entries(cmds)) {
            const fullId = setId + "." + cmdId;
            if (cmdIds.some((id) => id === cmdId || id === fullId || cmdId.startsWith(id + "."))) {
              matched[cmdId] = cmdDef;
            }
          }
          if (Object.keys(matched).length > 0) {
            const setCopy = { ...(cs as Record<string, unknown>) };
            setCopy.commands = matched;
            fcs[setId] = setCopy;
          }
        }
        if (doc.components) filtered.components = doc.components;
        process.stdout.write(JSON.stringify(filtered, null, 2) + "\n");
      }
      process.exit(0);
    });
  return program;
}
